import React, { useState } from 'react';
import { useStore } from '../context/StoreContext.tsx';
import { generateCaptions } from '../services/geminiService.ts';
import { AssetType, AssetStatus } from '../types.ts';
import { MessageSquare, Copy, RefreshCw, Save, Search, Tag, Trash2 } from 'lucide-react';

const CaptionVault: React.FC = () => {
  const { dna, addAsset, assets, deleteAsset } = useStore();

  const [context, setContext] = useState('');
  const [platform, setPlatform] = useState('Instagram');
  const [tone, setTone] = useState('Playful');
  const [captions, setCaptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchSaved, setSearchSaved] = useState('');

  // Get saved captions from Assets
  const savedCaptions = assets.filter(a => a.type === AssetType.Caption);
  const filteredSaved = savedCaptions.filter(c => 
    c.url.toLowerCase().includes(searchSaved.toLowerCase()) || 
    c.tags.some(t => t.toLowerCase().includes(searchSaved.toLowerCase()))
  );

  const handleGenerate = async () => {
    if (!context) return;
    setLoading(true);
    const results = await generateCaptions(context, platform, tone, dna);
    setCaptions(results);
    setLoading(false);
  };

  const handleSave = (text: string) => {
    addAsset({
      id: `cap-${Date.now()}`,
      url: text,
      type: AssetType.Caption,
      createdAt: Date.now(),
      scenario: 'Generated Caption',
      status: AssetStatus.Draft,
      tags: [platform.toLowerCase(), 'copy', tone.toLowerCase()],
      meta: {}
    });
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this caption?')) {
      try {
        await deleteAsset(id);
      } catch (e) {
        console.error("Failed to delete", e);
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto h-[calc(100vh-120px)] flex flex-col md:flex-row gap-6">
      
      {/* LEFT: Generator */}
      <div className="w-full md:w-1/2 flex flex-col gap-6 overflow-y-auto">
        <div>
          <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-1">Caption Vault</h1>
          <p className="text-[var(--text-secondary)]">Generate and store on-brand copy.</p>
        </div>
        
        <div className="bg-[var(--bg-primary)] p-6 rounded-xl border border-[var(--border-subtle)] shadow-sm space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Context / Topic</label>
            <textarea
              className="input-field"
              rows={3}
              placeholder="e.g. Chip celebrating a successful booth setup at CES..."
              value={context}
              onChange={(e) => setContext(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Platform</label>
              <select 
                className="input-field"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
              >
                <option>Instagram</option>
                <option>LinkedIn</option>
                <option>Twitter/X</option>
                <option>Email Subject</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Tone</label>
              <select 
                className="input-field"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
              >
                <option>Playful</option>
                <option>Professional</option>
                <option>Excited</option>
                <option>Educational</option>
              </select>
            </div>
          </div>
          
          <button
            onClick={handleGenerate}
            disabled={loading || !context}
            className="w-full py-3 bg-[var(--text-primary)] text-[var(--bg-primary)] rounded-lg font-bold hover:scale-[1.02] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <RefreshCw className="animate-spin" /> : <MessageSquare />}
            Generate Options
          </button>
        </div>

        {/* Generated Results */}
        {captions.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-sm font-bold text-[var(--text-tertiary)] uppercase tracking-wider">Results</h3>
            {captions.map((cap, idx) => (
              <div key={idx} className="p-4 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-subtle)] hover:border-[var(--accent-primary)] transition-colors group shadow-sm">
                <p className="text-[var(--text-primary)] text-sm whitespace-pre-wrap leading-relaxed">{cap}</p>
                <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-[var(--border-subtle)]">
                  <button 
                    onClick={() => navigator.clipboard.writeText(cap)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] bg-[var(--bg-tertiary)] hover:bg-[var(--border-subtle)] rounded-lg transition-colors"
                  >
                    <Copy size={14} /> Copy
                  </button>
                  <button 
                    onClick={() => handleSave(cap)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-hover)] rounded-lg shadow-sm transition-colors"
                  >
                    <Save size={14} /> Save to Vault
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* RIGHT: Saved Vault */}
      <div className="w-full md:w-1/2 bg-[var(--bg-secondary)] border-l border-[var(--border-subtle)] p-6 flex flex-col h-full overflow-hidden">
        <div className="mb-6">
           <h2 className="text-lg font-bold text-[var(--text-primary)] flex items-center gap-2">
             Saved Captions
           </h2>
           <div className="mt-4 relative">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" size={16} />
             <input 
               type="text" 
               placeholder="Search vault..."
               value={searchSaved}
               onChange={(e) => setSearchSaved(e.target.value)}
               className="input-field pl-10"
             />
           </div>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
           {filteredSaved.length > 0 ? filteredSaved.map(item => (
             <div key={item.id} className="bg-[var(--bg-primary)] p-4 rounded-xl border border-[var(--border-subtle)] shadow-sm hover:shadow-md transition-shadow group">
                <p className="text-sm text-[var(--text-primary)] whitespace-pre-wrap line-clamp-3 group-hover:line-clamp-none">{item.url}</p>
                <div className="flex items-center justify-between mt-3">
                   <div className="flex gap-2">
                      {item.tags.map(tag => (
                        <span key={tag} className="px-2 py-0.5 bg-[var(--bg-tertiary)] text-[var(--text-secondary)] text-[10px] rounded-full flex items-center gap-1">
                          <Tag size={10} /> {tag}
                        </span>
                      ))}
                   </div>
                   <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => navigator.clipboard.writeText(item.url)}
                        className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--accent-primary)] hover:bg-[var(--bg-tertiary)] rounded"
                      >
                        <Copy size={14} />
                      </button>
                      <button 
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 text-[var(--text-tertiary)] hover:text-red-500 hover:bg-red-500/10 rounded"
                      >
                        <Trash2 size={14} />
                      </button>
                   </div>
                </div>
             </div>
           )) : (
             <div className="text-center py-12 text-[var(--text-tertiary)]">
               <Save size={32} className="mx-auto mb-2 opacity-20" />
               <p className="text-sm">No saved captions yet.</p>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default CaptionVault;